import React, { useEffect, useState, useContext } from 'react';
import PropTypes from 'prop-types';
import parse from 'html-react-parser';
import http from 'lc-http';
import useWindowDimensions from '../../hooks/useWindowDimensions';
import useSetHeroImg from '../../hooks/useSetHeroImg';
import styles from './heroMemberLogin.module.scss';
import LoginWidget from '../loginWidget/loginWidget';
import { AppContext } from '../../context';
import { getSearchParamsObject, appendParamsToUrl } from '../../utils/util';

const DEFAULT_LOGIN = {
  loginText: '',
  cta: null,
  theme: '',
};
const DEFAULT_EXCLUDE_ETID_LIST = [];

function HeroMemberLogin({
  headline = '',
  subHeroText = '',
  image = null,
  mobImage = null,
  backgroundSize = 'cover',
  login = DEFAULT_LOGIN,
  excludeEtidList = DEFAULT_EXCLUDE_ETID_LIST,
}) {
  const loginCookie = useContext(AppContext);
  if (loginCookie) {
    window.location = login.cta ? login.cta.link : '/';
  }
  const [firstName, setFirstName] = useState('');
  const [widgetProps, setWidgetProps] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const defaultReferrerId = '490499';
  const { width } = useWindowDimensions() || {};
  const heroImg = useSetHeroImg(image, mobImage, width);

  useEffect(() => {
    // Get the parameters from the URL
    const currentUrl = window.location.search;
    const paramsObject = getSearchParamsObject(currentUrl);
    const etId = paramsObject?.etid || '';

    // Ensure referrerId is set from params or default
    const referrerId = paramsObject?.referrerId || defaultReferrerId;
    const loginRedirectURL = paramsObject?.login_url || login.cta.link;

    // Editting the referrerId to default or from the params
    paramsObject.referrerId = referrerId;

    // Deleting the keys from the object to not append in the final URL params
    delete paramsObject.login_url;

    // Create URL with parameters
    const url = appendParamsToUrl(loginRedirectURL, paramsObject);

    if (etId && !excludeEtidList.includes(etId)) {
      http
        .post('/api/member-apply-personalization', {
          token: etId,
        })
        .then((response) => {
          const { data } = response;
          setFirstName(data.actorFirstName ?? '');
          setWidgetProps({
            options: {
              theme: login.theme,
              targetURL: url,
              loginButtonText: login.cta ? login.cta.label : '',
              etid: etId,
              loginEmail: data.email ?? '',
            },
            onLogin: (err) => {
              if (!err) window.location = url;
            },
          });
          setIsLoading(false);
        })
        .catch(() => {
          setWidgetProps({
            options: {
              theme: login.theme,
              targetURL: url,
              loginButtonText: login.cta ? login.cta.label : '',
              etid: etId,
            },
            onLogin: (err) => {
              if (!err) window.location = url;
            },
          });
          setIsLoading(false);
        });
    } else {
      setWidgetProps({
        options: {
          theme: login.theme,
          targetURL: url,
          loginButtonText: login.cta ? login.cta.label : '',
          etid: false,
        },
        onLogin: (err) => {
          if (!err) window.location = url;
        },
      });
      setIsLoading(false);
    }
  }, [excludeEtidList, login]);

  return (
    <div
      id="heroMemberLogin"
      className={`${styles.heroMemberLogin} ${
        backgroundSize === 'contain' && styles.bgContain
      }`}
    >
      {heroImg
        ? parse(
            `<style>
  #heroMemberLogin{
      background-image: url('${heroImg?.src}');
    }
  </style>`
              .trim()
              .replace(/\s{2,}/g, '')
          )
        : null}
      <div className={styles.heroLoginPersonalizedPageContent}>
        <div className={styles.heroLoginPersonalizedContent}>
          <div className={styles.heroLandingPrequal}>
            <div
              className={`${styles.prequalModalEntry} ${styles.loginPrequal}`}
            >
              <h1 className={styles.heroLandingPrequalHeader}>
                <div>
                  {parse(
                    `${
                      headline
                        ? `${headline}${firstName ? ` ${firstName}` : ''},`
                        : ''
                    } ${subHeroText}`
                  )}
                </div>
              </h1>
            </div>
            {login.cta && !isLoading && <LoginWidget {...widgetProps} />}
            <p
              className={`${styles.loginPrequalFooter} ${styles.textCenter} ${styles.bold}`}
            >
              {parse(login.loginText)}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

HeroMemberLogin.propTypes = {
  headline: PropTypes.string,
  subHeroText: PropTypes.string,
  image: PropTypes.shape({
    src: PropTypes.string,
    alt: PropTypes.string,
  }),
  mobImage: PropTypes.shape({
    src: PropTypes.string,
    alt: PropTypes.string,
  }),
  backgroundSize: PropTypes.string,
  login: PropTypes.shape({
    loginText: PropTypes.string,
    cta: PropTypes.shape({
      label: PropTypes.string,
      link: PropTypes.string,
    }),
    theme: PropTypes.string,
  }),
  excludeEtidList: PropTypes.array,
};

export default HeroMemberLogin;
